import { guideHabitats } from "./fieldGuide.ts";
import { MOVES } from "./moves.ts";
import { routePlan } from "./routePlan.ts";
import { SPECIES } from "./species.ts";
import { TYPE_IDS, typeMod } from "./types.ts";
import type { SaveState } from "../game/save.ts";

/** Explain shared exposure to the next public trainer kit; no hidden rolls or save edits. */
export function crewCoverage(state: SaveState & { week?: number }) {
  const next = routePlan(state).next;
  const living = state.party.filter(m => m.hp > 0);
  const attacks = new Set(next.team.flatMap(m => SPECIES[m.species].moves)
    .map(id => MOVES[id]!).filter(m => m.power > 0).map(m => m.type));
  const risk = TYPE_IDS.filter(type => attacks.has(type)).map(type => ({
    type, count: living.filter(m => typeMod(type, SPECIES[m.speciesId].types) > 1).length,
    weight: living.reduce((sum, m) => sum + typeMod(type, SPECIES[m.speciesId].types), 0),
  })).filter(row => row.count >= 2).sort((a, b) => b.count - a.count || b.weight - a.weight)[0];
  if (!risk) return { next, living: living.length, total: state.party.length, risk: null, option: null };
  const cover = living.filter(m => typeMod(risk.type, SPECIES[m.speciesId].types) <= 1)
    .sort((a, b) => b.level - a.level)[0];
  if (cover) return { next, living: living.length, total: state.party.length, risk,
    option: { kind: "crew" as const, mon: cover, species: SPECIES[cover.speciesId] } };
  const boxed = state.pc.filter(m => typeMod(risk.type, SPECIES[m.speciesId].types) <= 1)
    .sort((a, b) => b.level - a.level)[0];
  if (boxed) return { next, living: living.length, total: state.party.length, risk,
    option: { kind: "box" as const, mon: boxed, species: SPECIES[boxed.speciesId] } };
  const growth = state.party.map(mon => ({ mon, species: SPECIES[mon.speciesId] }))
    .filter(({ mon, species }) => species.evolvesTo && species.evolveLevel! <= mon.level + 5
      && typeMod(risk.type, SPECIES[species.evolvesTo].types) <= 1)
    .sort((a, b) => (a.species.evolveLevel! - a.mon.level) - (b.species.evolveLevel! - b.mon.level))[0];
  if (growth) return { next, living: living.length, total: state.party.length, risk,
    option: { kind: "evolve" as const, ...growth, evolved: SPECIES[growth.species.evolvesTo!] } };
  const level = Math.max(1, ...living.map(m => m.level));
  const threatening = next.team.filter(foe => SPECIES[foe.species].moves.some(id => MOVES[id]!.power > 0 && MOVES[id]!.type === risk.type));
  const candidates = [...new Set(state.seen)].map(id => SPECIES[id])
    .filter(species => typeMod(risk.type, species.types) <= 1).flatMap(species => {
      const habitat = guideHabitats(species.id, state.defeatedTrainers.includes("vell"))
        .filter(row => row.hi <= level + 2).sort((a, b) => a.hi - b.hi)[0];
      const pressure = Math.max(...species.moves.map(id => MOVES[id]!).filter(m => m.power > 0)
        .map(move => Math.max(...threatening.map(foe => typeMod(move.type, SPECIES[foe.species].types)))));
      return habitat ? [{ species, habitat, pressure }] : [];
    }).sort((a, b) => b.pressure - a.pressure || a.habitat.hi - b.habitat.hi);
  return { next, living: living.length, total: state.party.length, risk,
    option: candidates[0] ? { kind: "seen" as const, ...candidates[0] } : null };
}
